import { useApolloClient } from '@apollo/client/react';
import { getBaseAvailabilityEntriesFromAssetAvailabilities } from 'pages/Availability/common/utils';
import { GET_ASSET_AVAILABILITIES } from 'pages/Availability/queries';
import {
  AssetAvailability,
  BaseAssetAvailabilityEntry,
  Site,
} from 'pages/Availability/types';
import { useEffect, useState } from 'react';
import { ApiName } from 'types/apiName';

interface UseAssetAvailabilitiesParams {
  site: Site;
  viewDateRange: {
    start: string;
    end: string;
  };
}

export const useAssetAvailabilities = ({
  site,
  viewDateRange,
}: UseAssetAvailabilitiesParams) => {
  const client = useApolloClient();
  const [availabilityEntriesFromApi, setAvailabilityEntriesFromApi] = useState<
    BaseAssetAvailabilityEntry[]
  >([]);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    const fetchAssetAvailabilities = async () => {
      if (!site.assets) return;

      try {
        const results = await Promise.all(
          site.assets.map((asset) =>
            client.query<{ assetAvailabilities: Array<AssetAvailability> }>({
              query: GET_ASSET_AVAILABILITIES,
              fetchPolicy: 'network-only',
              variables: {
                filter: {
                  and: [
                    { end: { gte: viewDateRange.start } },
                    { start: { lte: viewDateRange.end } },
                  ],
                  asset: {
                    sourceName: { eq: asset.sourceName },
                    sourceId: { eq: asset.sourceId },
                  },
                },
                sorting: [{ field: 'start', direction: 'DESC' }],
              },
              context: { apiName: ApiName.plutoAvailabilityApi },
            }),
          ),
        );

        const allAssetAvailabilities = results
          .flatMap(({ data }) => data?.assetAvailabilities ?? [])
          .sort(
            (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime(),
          );

        setAvailabilityEntriesFromApi(
          getBaseAvailabilityEntriesFromAssetAvailabilities(
            allAssetAvailabilities,
          ),
        );
      } catch {
        // Failed to fetch asset availabilities, reset to empty state
        setAvailabilityEntriesFromApi([]);
      }
    };

    fetchAssetAvailabilities();
  }, [
    site.assets,
    viewDateRange.start,
    viewDateRange.end,
    client,
    refetchTrigger,
  ]);

  return {
    availabilityEntriesFromApi,
    refetchAssetAvailabilities: () => {
      setRefetchTrigger((prev) => prev + 1);
    },
  };
};
